import { TabItem } from '@/components/layout/ModuleNavigation'

/**
 * Module slug for the Profitability module. The internal name stays
 * `financials` (routes, APIs, feature flag); only the display label says
 * Profitability.
 */
export const FINANCIALS_MODULE_SLUG = 'financials'

/**
 * The four module tabs (2026-09-16 consolidation of ten). Each tab is a
 * space: Overview is one page, the other three host their views behind a
 * Settings-style left menu (never a tab inside a tab).
 */
export type FinancialsModuleTab = 'overview' | 'forecast' | 'billing' | 'setup'

export type ForecastView = 'forecast' | 'scenarios'
export type BillingView =
  | 'upcoming'
  | 'monthly'
  | 'earnings'
  | 'contractor-invoices'
export type SetupView =
  | 'rates'
  | 'approvals'
  | 'budget-categories'
  | 'budget-templates'
  | 'overhead'

export const SETUP_SETTINGS_SECTIONS: readonly SetupView[] = [
  'budget-categories',
  'budget-templates',
  'overhead',
]

/** Segments (after /financials) that belong to each tab. */
const TAB_BY_SEGMENT: Record<string, FinancialsModuleTab> = {
  overview: 'overview',
  performance: 'overview',
  forecast: 'forecast',
  scenarios: 'forecast',
  billing: 'billing',
  monthly: 'billing',
  earnings: 'billing',
  setup: 'setup',
  rates: 'setup',
  approvals: 'setup',
  settings: 'setup',
}

function moduleSegments(pathname: string): string[] | null {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length < 2 || segments[1] !== 'financials') return null
  return segments.slice(2)
}

/**
 * Resolve the active module tab from a /:orgSlug/financials/* pathname.
 * Returns null when the path is not inside the module.
 */
export function resolveFinancialsTabFromPath(
  pathname: string,
): FinancialsModuleTab | null {
  const rest = moduleSegments(pathname)
  if (!rest) return null
  const [first] = rest
  if (!first) return 'overview'
  return TAB_BY_SEGMENT[first] ?? 'overview'
}

/** Which Forecast view a path shows (legacy /scenarios included). */
export function resolveForecastView(pathname: string): ForecastView {
  const rest = moduleSegments(pathname) ?? []
  if (rest[0] === 'scenarios') return 'scenarios'
  if (rest[0] === 'forecast' && rest[1] === 'scenarios') return 'scenarios'
  return 'forecast'
}

/** Which Billing view a path shows (legacy /monthly and /earnings included). */
export function resolveBillingView(pathname: string, search = ''): BillingView {
  const rest = moduleSegments(pathname) ?? []
  if (rest[0] === 'monthly') return 'monthly'
  if (rest[0] === 'earnings') {
    const legacyTab = new URLSearchParams(search).get('tab')
    return rest[1] === 'invoices' || legacyTab === 'invoices'
      ? 'contractor-invoices'
      : 'earnings'
  }
  if (rest[0] === 'billing') {
    if (rest[1] === 'monthly') return 'monthly'
    if (rest[1] === 'earnings') return 'earnings'
    if (rest[1] === 'contractor-invoices') return 'contractor-invoices'
  }
  return 'upcoming'
}

const isSetupView = (value: string | undefined): value is SetupView =>
  value === 'rates' ||
  value === 'approvals' ||
  value === 'budget-categories' ||
  value === 'budget-templates' ||
  value === 'overhead'

/**
 * Which Setup view a path shows. The in-module Settings address
 * (/financials/settings?section=) stays valid for the settings hub and omni
 * search; it resolves to the matching budget section.
 */
export function resolveSetupView(pathname: string, search = ''): SetupView {
  const rest = moduleSegments(pathname) ?? []
  if (rest[0] === 'rates') return 'rates'
  if (rest[0] === 'approvals') return 'approvals'
  if (rest[0] === 'settings') {
    const section = new URLSearchParams(search).get('section') ?? undefined
    return isSetupView(section) && SETUP_SETTINGS_SECTIONS.includes(section)
      ? section
      : 'budget-categories'
  }
  if (rest[0] === 'setup' && isSetupView(rest[1])) return rest[1]
  return 'rates'
}

/** Canonical module paths, relative to the org root. */
export const financialsPaths = {
  overview: () => '/financials/overview',
  performance: () => '/financials/overview#performance',
  forecast: () => '/financials/forecast',
  scenarios: (scenarioId?: string | null) =>
    scenarioId
      ? `/financials/forecast/scenarios/${scenarioId}`
      : '/financials/forecast/scenarios',
  billing: (view: BillingView = 'upcoming') =>
    view === 'upcoming' ? '/financials/billing' : `/financials/billing/${view}`,
  rates: (subTab?: string | null) =>
    subTab ? `/financials/setup/rates/${subTab}` : '/financials/setup/rates',
  approvals: () => '/financials/setup/approvals',
  setupSection: (section: SetupView) => `/financials/setup/${section}`,
}

/**
 * Rewrite a legacy module URL (the ten-tab layout) onto its canonical home.
 * Input and output are org-relative paths without the org slug; the query
 * string is passed through. Returns null when the path is already current.
 */
export function legacyFinancialsRedirect(
  pathname: string,
  search = '',
): { pathname: string; search: string; hash?: string } | null {
  const rest = moduleSegments(pathname)
  if (!rest) return null
  const [first, ...deeper] = rest
  const params = new URLSearchParams(search)
  const withSearch = (target: string, hash?: string) => {
    const query = params.toString()
    return { pathname: target, search: query ? `?${query}` : '', hash }
  }

  switch (first) {
    case 'performance':
      return withSearch(financialsPaths.overview(), '#performance')
    case 'scenarios':
      return withSearch(financialsPaths.scenarios(deeper[0]))
    case 'monthly':
      return withSearch(financialsPaths.billing('monthly'))
    case 'earnings': {
      const invoices =
        deeper[0] === 'invoices' || params.get('tab') === 'invoices'
      params.delete('tab')
      return withSearch(
        financialsPaths.billing(invoices ? 'contractor-invoices' : 'earnings'),
      )
    }
    case 'rates': {
      // /rates/<view>[/…] keeps its view; the bare /rates?tab= form carried
      // the view in the query and the Rates page still canonicalizes it.
      const suffix = deeper.length ? `/${deeper.join('/')}` : ''
      return withSearch(`${financialsPaths.rates()}${suffix}`)
    }
    case 'approvals':
      return withSearch(financialsPaths.approvals())
    default:
      return null
  }
}

const isTabActive = (currentPath: string, tab: FinancialsModuleTab): boolean =>
  resolveFinancialsTabFromPath(currentPath) === tab

/**
 * Launch bar: four tabs. Overview (this period, Performance and budget
 * Approvals stacked below; it carries the approvals badge), Forecast
 * (forecast + scenarios), Billing (upcoming, monthly, earnings, contractor
 * invoices) and Settings (rates, budget settings; path /setup).
 */
export const getFinancialsTabs = (counts?: {
  approvals?: number
}): TabItem[] => {
  return [
    {
      label: 'Overview',
      path: '/overview',
      id: 'overview',
      // Budget approvals are a work queue on Overview, so the badge is here.
      count: counts?.approvals,
      isActive: (currentPath: string) => isTabActive(currentPath, 'overview'),
    },
    {
      label: 'Forecast',
      path: '/forecast',
      id: 'forecast',
      isActive: (currentPath: string) => isTabActive(currentPath, 'forecast'),
    },
    {
      label: 'Billing',
      path: '/billing',
      id: 'billing',
      isActive: (currentPath: string) => isTabActive(currentPath, 'billing'),
    },
    {
      // Rates, roles and budget configuration: the module's settings, named
      // like every other module's. The path stays /setup for links.
      label: 'Settings',
      path: '/setup',
      id: 'setup',
      isActive: (currentPath: string) => isTabActive(currentPath, 'setup'),
    },
  ]
}
